import { useWallet } from '@solana/wallet-adapter-react';
import { Button } from '@/components/ui/button';
import { useSupabase } from '@/components/auth/auth-provider';
import { useState } from 'react';
import nacl from 'tweetnacl';
import { useQueryClient } from '@tanstack/react-query';

export function LinkWalletButton() {
  const { session } = useSupabase();
  const { publicKey, signMessage, connected } = useWallet();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  // This button should only be visible if the user is logged in with Web2
  // AND they do not already have a wallet linked.
  if (!session) {
    return null;
  }

  const handleLinkWallet = async () => {
    if (!connected || !publicKey || !signMessage) {
      console.error('Wallet not connected or signMessage not available. Please connect your wallet first.');
      // Optionally, prompt the user to connect their wallet here.
      return;
    }

    setIsLoading(true);
    try {
      // Create a nonce to prevent replay attacks
      const nonce = new Date().getTime();
      const message = `Link this wallet to your account. Nonce: ${nonce}`;
      const messageBytes = new TextEncoder().encode(message);

      const signature = await signMessage(messageBytes);

      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://obelisk-skiller-production.up.railway.app';
      const token = localStorage.getItem('runara_auth_token');
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${backendUrl}/api/profiles/link-wallet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          message,
          signature: Buffer.from(signature).toString('base64'),
          _publicKey: publicKey.toBase58(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to link wallet.');
      }

      console.log('Wallet linked successfully');
      // Invalidate the specific query to force a refetch.
      await queryClient.invalidateQueries({ queryKey: ['player-account', publicKey?.toBase58(), session?.user?.id] });

    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button onClick={handleLinkWallet} disabled={isLoading || !connected}>
      {isLoading ? 'Linking...' : 'Link Wallet'}
    </Button>
  );
}